import { MeetingSlot } from "@closerplatform/spinner-openapi";
import classNames from "classnames";
import { addDays, isToday, startOfToday, subDays } from "date-fns";
import { debounce } from "lodash";
import React from "react";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { getFriendlyDateString, Mode } from "@fidget/common/utils/timeFormatter";
import { WidgetButton } from "@fidget/common/components/widgetButton/widgetButton";
import styles from "./meetingSlotPicker.css";

/** @type {Number} Slot container's scrollPos value after which the slot container is considered 'scrolled' */
const SCROLLED_THRESHOLD = 0;

interface Props {
  /** @type {string} Store token in component to enable seamless switching to new scheduling process and slot reloading */
  token: string;
  /** Callback to get the slots with */
  slotsProvider: (token: string, start: number, end: number) => Promise<Array<MeetingSlot>>;
  /** Callback invoked when the user clicks the Cancel button */
  onCancel: (token: string) => void;
  /** Callback invoked then the user selects a slot and clicks Submit button */
  onSlotPicked: (token: string, start: number, duration: number) => void;
}

interface State {
  /** @type {Date} Beginning of the first day for the first column of slots */
  startDate: Date;
  slots: Array<MeetingSlot>;
  selectedSlot?: MeetingSlot;
  showScrollHint: boolean;
  /** @type {boolean} Has the user scrolled down the slots container so we should show the border below header? */
  scrolled: boolean;
}

/** @type {Number} Time span for a single slots column, in microseconds */
const TIMESTAMP_DAY_OFFSET = 60 * 60 * 24 * 1000;

/**
 * Component for choosing the meeting slot based on the pending meeting token
 */
export class MeetingSlotPicker extends React.Component<Props, State> {
  /** @type {Date} Stores start/minimum date for displaying columns and fetching the slots */
  private today: Date;

  constructor(props: Props) {
    super(props);
    this.today = startOfToday();
    this.state = {
      startDate: this.today,
      slots: [],
      showScrollHint: true,
      scrolled: false
    };
    this.loadSlots(this.props.token, this.today);
  }

  componentDidUpdate(prevProps: Props) {
    // if the token has changed, immediately fetch new slots starting from today
    if (prevProps.token !== this.props.token) {
      this.loadSlots(this.props.token, this.today);
    }
  }

  /**
   * Load slots for the next 3 days starting at start date and
   * update state accordingly
   * @param  {string} token pending meeting token
   * @param  {Date}   start start Date
   */
  loadSlots = async (token: string, start: Date) => {
    const { slotsProvider } = this.props;
    const now = new Date().getTime();
    const slots = await slotsProvider(
      token,
      Math.max(start.getTime(), now),
      addDays(start, 3).getTime()
    );
    this.setState({
      startDate: start,
      slots,
      selectedSlot: undefined
    });
  };

  /**
   * Handle click on left nav arrow
   */
  onPrevNavClick = (ev: React.SyntheticEvent) => {
    const { startDate } = this.state;
    if (startDate > this.today) {
      this.loadSlots(this.props.token, subDays(startDate, 3));
    }
  };

  /**
   * Handle click on right nav arrow
   */
  onNextNavClick = (ev: React.SyntheticEvent) => {
    this.loadSlots(this.props.token, addDays(this.state.startDate, 3));
  };

  /**
   * Handle click on one a meeting slot
   * @param  {MeetingSlot} slot clicked meeting slot
   */
  onSlotClick = (slot: MeetingSlot) => {
    this.setState({
      selectedSlot: slot
    });
  };

  /**
   * Handle click on Submit button
   */
  onSubmitClick = () => {
    const { selectedSlot } = this.state;
    const { token, onSlotPicked } = this.props;
    if (selectedSlot) {
      onSlotPicked(token, selectedSlot.start, selectedSlot.duration);
    }
  };

  /**
   * Handle click on scroll info message's aknowledgement/toggle button
   * @param  {React.SyntheticEvent} ev Click event
   */
  toggleScrollHint = (ev: React.SyntheticEvent) => {
    this.setState({ showScrollHint: !this.state.showScrollHint });
  };

  /**
   * Handle scroll container's scroll event
   * Because synthetic event's target is nullified and we need the call to be
   * debounced/throttled, we pass the container scroll pos to debounced method
   * that contains the proper logic
   * @param  {React.UIEvent} ev scroll event
   */
  onSlotsScroll = (ev: React.UIEvent) =>
    this.setScrollState((ev.target as HTMLDivElement).scrollTop);

  /**
   * Actual handler for the slot container's scroll pos change
   * @param  {Number} pos  Slot container scroll position
   */
  setScrollState = debounce(
    (pos: number) => {
      this.setState({ scrolled: pos > SCROLLED_THRESHOLD });
    },
    200,
    { leading: true }
  );

  /**
   * Get the text to display in the column header
   * @param  {Date} date start date for slot column
   * @return {string} localized header text
   */
  getColumnHeaderText = (date: Date) =>
    getFriendlyDateString(date.getTime(), {
      useToday: true,
      useTomorrow: true,
      mode: Mode.SHORT,
      useWeekday: true
    });

  /**
   * Render a single column of slots
   * @param  {Date} startDate  start date for the slots displayed in the column
   * @return {JSX.Element} column as JSX node
   */
  renderSlotList = (startDate: Date) => {
    const start = startDate.getTime();
    const end = start + TIMESTAMP_DAY_OFFSET;
    const slots = this.state.slots
      .filter((slot) => start <= slot.start && slot.start < end)
      .sort((a, b) => a.start - b.start);
    return (
      <ul>
        {slots.map((slot) => (
          <li key={`${slot.start}-${slot.duration}`}>
            <button
              type="button"
              onClick={() => this.onSlotClick(slot)}
              className={classNames({
                [styles["selected-slot"]]: this.state.selectedSlot === slot
              })}
            >
              {intl.formatTime(slot.start)} {"-"} {intl.formatTime(slot.start + slot.duration)}
            </button>
          </li>
        ))}
      </ul>
    );
  };

  /** Render component */
  render() {
    const { startDate, showScrollHint, selectedSlot, scrolled } = this.state;
    return (
      <div className={classNames(styles.container, { [styles.scrolled]: scrolled })}>
        <div className={styles.header}>
          <button type="button" onClick={this.onPrevNavClick} disabled={isToday(startDate)}>
            <i className="icon-chevron-left"></i>
          </button>
          <span>{this.getColumnHeaderText(startDate)}</span>
          <span>{this.getColumnHeaderText(addDays(startDate, 1))}</span>
          <span>{this.getColumnHeaderText(addDays(startDate, 2))}</span>
          <button type={"button"} onClick={this.onNextNavClick}>
            <i className="icon-chevron-right"></i>
          </button>
        </div>
        <div className={styles.slots} onScroll={this.onSlotsScroll}>
          <div>
            {this.renderSlotList(startDate)}
            {this.renderSlotList(addDays(startDate, 1))}
            {this.renderSlotList(addDays(startDate, 2))}
          </div>
        </div>

        <div className={styles.footer}>
          {showScrollHint && (
            <div className={styles.scrollHint}>
              <L10n id={"widget.meeting.scrollHint"} />
              <button type={"button"} onClick={this.toggleScrollHint}>
                <L10n id={"widget.meeting.hideScrollHint"} />
              </button>
            </div>
          )}

          <div>
            <WidgetButton
              onClick={() => this.props.onCancel(this.props.token)}
              type="button"
              buttonStyle={"secondary"}
              fullWidth
              small
            >
              <L10n id="widget.cancel" />
            </WidgetButton>
          </div>
          <div>
            <WidgetButton
              onClick={this.onSubmitClick}
              disabled={!selectedSlot}
              type="button"
              buttonStyle={"primary"}
              fullWidth
              small
            >
              <L10n id="widget.submit" />
            </WidgetButton>
          </div>
        </div>
      </div>
    );
  }
}
